import { Vue, Component, Watch, Mixins } from "vue-property-decorator";
import SearchBar from "../components/search-bar";
import { sheetExport, changeCaviteStatusByQuery } from '@/lib/services';
import Query from "@/lib/models/query";
import CavCavite from '@/lib/models/cav-cavite';
import XTable, { TableSortState } from '@/components/x-table';
import XTh from '@/components/x-table/x-th';
import { Getter, State, Action } from 'vuex-class';
import ActionTd from '@/components/x-table/action-td';
import { lexLabel, loadingClass } from "@/lib/helpers";
import User, { FLEX_PROFILES } from '@/lib/models/user';
import BeanQueryResult from '@/lib/models/bean-query-result';
import ActionTH from '@/components/x-table/action-th';
import Icon from '@/components/icon';
import Dropdown from '@/components/dropdown';
import { STATUT_VALIDE, STATUT_REFUSE } from '@/lib/constants/validation';
import { RootState } from '@/store';
import RightsAware from '@/lib/mixins/rights-aware';


const PAGE_SIZE = 15;

@Component
export default class HomeIndex extends Mixins(RightsAware) {

  @Action logout;
  @Action refreshLastFetched;
  @Action retrieveCavitesByQuery;
  @Action setCurrentPage;

  @Getter getLexValue;
  @Getter userhHasHigherFlexProfileThan;
  @Getter loggedUser: User;

  @State((s: RootState) => s.home.userCavites) userCavites: CavCavite[];
  @State((s: RootState) => s.home.loading.userCavites) userCavitesIsLoading: number;
  @State((s: RootState) => s.home.loading.queriedCavites) queriedCavitesIsLoading: number;
  @State((s: RootState) => s.home.query) query: Query;
  @State((s: RootState) => s.home.lastRetrieveCavitesByQueryBody) cachedQueryBody;
  @State((s: RootState) => s.home.queryResult) queryResult: BeanQueryResult;
  @State((s: RootState) => s.home.currentPage) currentPage: number;

  protected mainSort: TableSortState = {prop: "numCavite", order: "asc"};
  protected selectedCavIds: string[] = [];
  protected noSearch: boolean = true;

  get userIsPoc() {
    return this.userhHasHigherFlexProfileThan(FLEX_PROFILES.POC_LECTEUR);
  }

  get pagination(): {value: number, label: string, className?: string}[]  {
    const nbPages = Math.floor(this.queryResult.totalSize / PAGE_SIZE) + 1;
    const numbers = Array.from({length: nbPages}, (_, i) => i).slice(Math.max(0, this.currentPage - 2), Math.min(nbPages, Math.max(4, this.currentPage + 2)))
      .map(v => ({value: v, label: `${v + 1}`, className: "page-number"}));
    return [
      this.currentPage > 0 ? {label: "<<", value: 0} : null,
      this.currentPage > 0 ? {label: "<", value: this.currentPage - 1} : null,
      ...numbers,
      nbPages > 1 && this.currentPage < nbPages - 1 ? {label: ">", value: this.currentPage + 1} : null,
      nbPages > 1 && this.currentPage < nbPages - 1 ? {label: ">>", value: nbPages - 1} : null
    ].filter(Boolean);
  }

  async mounted() {}

  async onSearchCommit(query: Query) {
    if (query.notEmpty) {
      query.sortField = this.mainSort.prop;
      query.sortOrder = this.mainSort.order;
      await this.retrieveCavitesByQuery(0);
    }
  }

  async onClickPage(idx) {
    await this.setCurrentPage(idx);
  }

  @Watch("queryResult")
  onResultsChange(next: BeanQueryResult) {
    this.noSearch = false;
    this.selectedCavIds = this.selectedCavIds.filter(id => next && next.cavites.some(({numCavite}) => id === numCavite));
  }

  @Watch("loggedUser", {immediate: true})
  onLoggedUserChange() {
    this.readonly_ = this.userhHasHigherFlexProfileThan(FLEX_PROFILES.AGENT_SAISIE);
  }

  onClickAddCavite() {
    this.$router.push({name: "cavite.edit", params: {id: "new"}});
  }

  onClickEditCavite(cav: CavCavite) {
    this.$router.push({name: "cavite.edit", params: {id: cav.numCavite}});
  }

  onToggleSelectAll(select) {
    this.selectedCavIds = select ? this.queryResult.cavites.map(({numCavite}) => numCavite) : [];
  }

  cavIsSelected({numCavite}) {
    return this.selectedCavIds.some(id => id === numCavite);
  }

  toggleCavSelection({numCavite}, e) {
    if (!e.target.checked && this.cavIsSelected({numCavite})) {
      this.selectedCavIds = this.selectedCavIds.filter(id => id !== numCavite);
    } else if (e.target.checked) {
      this.selectedCavIds = [...this.selectedCavIds, numCavite];
    }
  }

  async onExportCsv() {
    await sheetExport({
      format: this.userIsPoc ? "xls" : "csv",
      query: this.query,
      poc: this.userIsPoc
    });
  }

  onMainSort(sortState) {
    this.mainSort = sortState;
    this.onSearchCommit(this.query);
  }

  async onClickBatchUpdate(useSelection, what) {
    let query;
    if (useSelection) {
      query = new Query().inject({
        numCavites: this.selectedCavIds
      });
    } else {
      query = this.cachedQueryBody.query;
    }
    await changeCaviteStatusByQuery({...what, user: this.loggedUser, query});
    await this.refreshLastFetched();
  }

  async onClickLogout() {
    await this.logout();
    location.reload();
  }

  getStatutColorClass(v) {
    return {
      0: "refuse",
      1: "nonvise",
      2: "valide"
    }[v];
  }

  renderMainTable() {
    return (
      <XTable class={`table is-striped is-fullwidth ${loadingClass(this.queriedCavitesIsLoading)}`}
        data={this.queryResult.cavites}
        sortAuto={false}
        defaultSortState={this.mainSort}
        onSort={this.onMainSort}
        scopedSlots={{
          noResult: () => this.noSearch ? "Utilisez le formulaire pour rechercher des cavités" : null,
          header: () => (
            <tr>
              <XTh sortable={false}></XTh>
              <XTh prop="numCavite">Id</XTh>
              <XTh prop="nomCavite">Nom</XTh>
              <XTh prop="typeCavite">Type</XTh>
              <XTh prop="commune.numDept">Dept.</XTh>
              <XTh prop="commune.nom">Commune</XTh>
              <XTh prop="xouv93">XL93</XTh>
              <XTh prop="youv93">YL93</XTh>
              <XTh prop="confidentialite">Confidentialité</XTh>
              <XTh prop="statutSaisie" sortable={false}>Validation</XTh>
              <XTh prop="statutDiffusion" sortable={false}>Diffusion</XTh>
              <ActionTH/>
            </tr>
          ),
          row: ({d}: {d: CavCavite}) => (
            <tr>
              <td><input type="checkbox"
                checked={this.cavIsSelected(d)}
                onChange={e => this.toggleCavSelection(d, e)}/></td>
              <td>{d.numCavite}</td>
              <td>{d.nomCavite}</td>
              <td>{d.typeCavite}</td>
              <td>{(d.commune && d.commune.numDept) || "-"}</td>
              <td>{(d.commune && d.commune.nom) || "-"}</td>
              <td>{d.xouv93}</td>
              <td>{d.youv93}</td>
              <td>{d.confidentialite}</td>
              <td><span class={`tag ${this.getStatutColorClass(d.statutSaisie)}`}>{lexLabel(this.getLexValue("StatutValidation", d.statutSaisie))}</span></td>
              <td><span class={`tag ${this.getStatutColorClass(d.statutDiffusion)}`}>{lexLabel(this.getLexValue("StatutValidation", d.statutDiffusion))}</span></td>
              <ActionTd withDelete={false}
                onEdit={() => this.onClickEditCavite(d)}
                onOpen={() => this.onClickEditCavite(d)}
              />
            </tr>
          )
        }}>
      </XTable>
    );
  }

  renderUserTable() {
    return (
      <XTable class={`table is-striped is-fullwidth ${loadingClass(this.userCavitesIsLoading)}`}
        data={this.userCavites}
        scopedSlots={{
          header: () => (
            <tr>
              <XTh prop="numCavite">Id</XTh>
              <XTh prop="nomCavite">Nom</XTh>
              <XTh prop="typeCavite">Type</XTh>
              <XTh prop="commune.numDept">Dept.</XTh>
              <XTh prop="commune.nom">Commune</XTh>
              <XTh prop="xouv93">XL93</XTh>
              <XTh prop="youv93">YL93</XTh>
              <XTh prop="confidentialite">Confidentialité</XTh>
              <XTh sortable={false}>Validation</XTh>
              <XTh sortable={false}>Diffusion</XTh>
              <ActionTH/>
            </tr>
          ),
          row: ({d}: {d: CavCavite}) => (
            <tr>
              <td>{d.numCavite}</td>
              <td>{d.nomCavite}</td>
              <td>{d.typeCavite}</td>
              <td>{(d.commune && d.commune.numDept) || "-"}</td>
              <td>{(d.commune && d.commune.nom) || "-"}</td>
              <td>{d.xouv93}</td>
              <td>{d.youv93}</td>
              <td>{d.confidentialite}</td>
              <td><span class={`tag ${this.getStatutColorClass(d.statutSaisie)}`}>{lexLabel(this.getLexValue("StatutValidation", d.statutSaisie))}</span></td>
              <td><span class={`tag ${this.getStatutColorClass(d.statutDiffusion)}`}>{lexLabel(this.getLexValue("StatutValidation", d.statutDiffusion))}</span></td>
              <ActionTd withDelete={false}
                onEdit={() => this.onClickEditCavite(d)}
              />
            </tr>
          )
        }}>
      </XTable>
    );
  }

  render() {
    return (
    <section class="main-container card">
      <div class="main-container-inner">
        <div class="top-bar v-section has-background-primary">
          <span class="title">Base de données des Cavités</span>
          <div class="right-buttons is-airy">
            {this.loggedUser.hasMinimumFlexProfile(FLEX_PROFILES.REF_THEM) && (
            <router-link tag="button" class="button is-small" to={{name: "admin.index"}}>Administration</router-link>
            )}
            <button class="button is-small logout-button" onClick={this.onClickLogout}>
              <Icon icon="sign-out" class="icon grey white-bis"/>
              <span>{this.loggedUser.mail} - Déconnexion</span>
            </button>
          </div>
        </div>
        <div class="main-table-container v-section">
          <SearchBar query={this.query} isSearching={Boolean(this.queriedCavitesIsLoading)} onCommit={this.onSearchCommit}/>
          <div class="main-table scrollable-table-wrapper">
            {this.renderMainTable()}
          </div>
          <div class="table-action-toolbar">
            <div class="table-action-toolbar-left is-flex flex-column justify-space-between">
              {!this.isReadonly && [
              <div>
                <a class="is-size-7" onClick={() => this.onToggleSelectAll(true)}>Tout cocher</a> / <a class="is-size-7" onClick={() => this.onToggleSelectAll(false)}>Tout décocher</a>
              </div>,
              <div class="is-flex is-airy">
                <Dropdown class="list-dropdown basic" placement="bottom-end"
                  disabled={this.selectedCavIds.length === 0}
                  scopedSlots={{
                    trigger: () => <a class="action">
                      <span class="trigger-label">Pour la sélection</span>
                      <Icon icon="caret-down" class="icon caret-icon grey"/>
                    </a>,
                    layer: () => (
                      <div class="list-dropdown__layer">
                        <ul>
                          <li class="dd-option is-danger"
                            onClick={() => this.onClickBatchUpdate(true, {statutSaisie: STATUT_VALIDE})}>
                              Valider la saisie
                          </li>
                          <li class="dd-option"
                            onClick={() => this.onClickBatchUpdate(true, {statutSaisie: STATUT_REFUSE})}>
                              Refuser la saisie
                          </li>
                          {this.loggedUser.hasMinimumFlexProfile(FLEX_PROFILES.REF_THEM) && [
                            <li class="dd-option"
                              onClick={() => this.onClickBatchUpdate(true, {statutDiffusion: STATUT_VALIDE})}>
                                Valider la diffusion
                            </li>,
                            <li class="dd-option"
                              onClick={() => this.onClickBatchUpdate(true, {statutDiffusion: STATUT_REFUSE})}>
                                Refuser la diffusion
                            </li>
                          ]}
                        </ul>
                      </div>
                    )
                  }}/>
                <Dropdown class="list-dropdown basic" placement="bottom-end"
                  disabled={this.queryResult.totalSize === 0}
                  scopedSlots={{
                    trigger: () => <a class="action">
                      <span class="trigger-label">Pour l'ensemble des résultats</span>
                      <Icon icon="caret-down" class="icon caret-icon grey"/>
                    </a>,
                    layer: () => (
                      <div class="list-dropdown__layer">
                        <ul>
                          <li class="dd-option" onClick={this.onExportCsv}>
                            <Icon icon="file-excel" class="icon grey"/>
                            <span>Exporter au format {this.userIsPoc ? 'XLS' : 'CSV'}</span>
                          </li>
                          <li><hr style="margin: 0.25rem 0"/></li>
                          <li class="dd-option is-danger"
                            onClick={() => this.onClickBatchUpdate(false, {statutSaisie: STATUT_VALIDE})}>
                              Valider la saisie
                          </li>
                          <li class="dd-option"
                            onClick={() => this.onClickBatchUpdate(false, {statutSaisie: STATUT_REFUSE})}>
                              Refuser la saisie
                          </li>
                          {this.loggedUser.hasMinimumFlexProfile(FLEX_PROFILES.REF_THEM) && [
                            <li class="dd-option"
                              onClick={() => this.onClickBatchUpdate(false, {statutDiffusion: STATUT_VALIDE})}>
                                Valider la diffusion
                            </li>,
                            <li class="dd-option"
                              onClick={() => this.onClickBatchUpdate(false, {statutDiffusion: STATUT_REFUSE})}>
                                Refuser la diffusion
                            </li>
                          ]}
                        </ul>
                      </div>
                    )
                  }}/>
              </div>
            ]}
            </div>
            <div class="table-action-toolbar-right">
              <ul class="cav-pagination">
                {this.pagination.map(({label, value, className = ""}) => (
                  <li class={`cav-pagination-page ${value === this.currentPage ? 'is-active' : ''} ${className}`} onClick={() => this.onClickPage(value)}>{label}</li>
                ))}
                {this.queryResult && (
                <li class="total-results has-text-grey">
                  <i>Total : {this.queryResult.totalSize}</i>
                </li>
                )}
              </ul>
              {!this.isReadonly && (
                <button class="create-cavite-btn button is-primary"
                  onClick={() => this.onClickAddCavite()}>
                    Ajouter une cavité
                </button>
              )}
            </div>
          </div>
        </div>
        {!this.isReadonly && (
        <div class="user-table-container v-section is-flex flex-column">
          <h3>Mes dernières cavités saisies</h3>
          <div class="main-table scrollable-table-wrapper">
            {this.renderUserTable()}
          </div>
        </div>
        )}
      </div>
      <router-view/>
    </section>
    );
  }
}

// VUE JSX HOT LOADER //
if (module.hot) require("/Users/home/j2ee/projects/cavites/src/main/client/node_modules/vue-jsx-hot-loader/src/api.js")({ Vue: require('vue'), ctx: eval('this'), module: module, hotId: "_vue_jsx_hot-da2805d8/index.tsx" });